import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from '../../../../components/ui/Dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../../components/ui/Table';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import { Spinner } from '../../../../components/ui/Spinner';
import AppIcon from '../../../../components/AppIcon';
import { PageLayout } from '../../../../components/ui/PageLayout';
import { Heart } from 'lucide-react';
import { useToast, ToastRenderer } from '../../../../hooks/useToast';
import { useDialogError, DialogErrorAlert } from '../../../../components/ui/DialogError';
import { CompanyRecognitionValueView, CompanyRecognitionValueInput } from '../../../../models/preformanceManagement';
import {
  getCompanyRecognitionValues,
  addCompanyRecognitionValue,
  updateCompanyRecognitionValue,
  deleteCompanyRecognitionValue,
} from '../../../../api/PreformanceManagement/Recognition';

const RecognitionValues: React.FC = () => {
  const { t } = useTranslation(['performance', 'common']);
  const { toast, showToast, dismissToast } = useToast();
  // CO-49: refused writes must be explained inside the dialog that refused them.
  const dialogError = useDialogError();
  const deleteDialogError = useDialogError();

  const [values, setValues] = useState<CompanyRecognitionValueView[]>([]);
  const [filteredValues, setFilteredValues] = useState<CompanyRecognitionValueView[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState<'add' | 'edit'>('add');
  const [currentInput, setCurrentInput] = useState<CompanyRecognitionValueInput>({ name: '', description: '' });
  const [editId, setEditId] = useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<CompanyRecognitionValueView | null>(null);
  const [search, setSearch] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getCompanyRecognitionValues();
      setValues(response.payload || []);
      setFilteredValues(response.payload || []);
    } catch (error: any) {
      showToast(error?.errors?.[0]?.description || t('Failed to load recognition values'), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const filtered = values.filter((v) =>
      v.name.toLowerCase().includes(search.toLowerCase()) ||
      (v.description && v.description.toLowerCase().includes(search.toLowerCase()))
    );
    setFilteredValues(filtered);
  }, [search, values]);

  const handleOpenAddDialog = () => {
    setCurrentInput({ name: '', description: '' });
    setDialogType('add');
    setEditId(null);
    dialogError.clear();
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (value: CompanyRecognitionValueView) => {
    setCurrentInput({ name: value.name, description: value.description });
    setDialogType('edit');
    setEditId(value.id);
    dialogError.clear();
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentInput({ name: '', description: '' });
    setEditId(null);
    dialogError.clear();
  };

  const handleSave = async () => {
    if (!currentInput.name.trim()) {
      dialogError.showMessages([t('Recognition value name is required')]);
      showToast(t('Recognition value name is required'), 'error');
      return;
    }

    setLoading(true);
    dialogError.clear();
    try {
      if (dialogType === 'add') {
        await addCompanyRecognitionValue(currentInput);
        showToast(t('Recognition value added successfully'), 'success');
      } else if (editId !== null) {
        await updateCompanyRecognitionValue(editId, currentInput);
        showToast(t('Recognition value updated successfully'), 'success');
      }
      handleCloseDialog();
      loadData();
    } catch (error: any) {
      const parsed = dialogError.capture(error, t('Failed to save recognition value'));
      showToast(parsed.messages[0], 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteDialog = (value: CompanyRecognitionValueView) => {
    setSelectedValue(value);
    deleteDialogError.clear();
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedValue(null);
    deleteDialogError.clear();
  };

  const handleDelete = async () => {
    if (!selectedValue) return;

    setLoading(true);
    deleteDialogError.clear();
    try {
      await deleteCompanyRecognitionValue(selectedValue.id);
      showToast(t('Recognition value deleted successfully'), 'success');
      handleCloseDeleteDialog();
      loadData();
    } catch (error: any) {
      const parsed = deleteDialogError.capture(error, t('Failed to delete recognition value'));
      showToast(parsed.messages[0], 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout
      title={t('Recognition Values')}
      icon={Heart}
      accentColor="rose"
      headerActions={(
        <Button variant="outline" iconName="PlusCircle" onClick={handleOpenAddDialog}>
          {t('common:Add')}
        </Button>
      )}
    >
      <div className="flex flex-col flex-1 gap-5">
      {/* Search */}
      <Input
        label={t('common:Search')}
        value={search}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
      />

      {/* Content */}
      {loading && !openDialog ? (
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      ) : filteredValues.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-6 text-center">
          <p className="text-muted-foreground">
            {search ? t('No Recognition values found matching your search') : t('No Recognition values created yet')}
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-semibold">{t('Name')}</TableHead>
                <TableHead className="font-semibold">{t('Description')}</TableHead>
                <TableHead className="font-semibold text-end">{t('common:Actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredValues.map((value) => (
                <TableRow key={value.id}>
                  <TableCell>{value.name}</TableCell>
                  <TableCell>{value.description || '-'}</TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenEditDialog(value)}
                        aria-label={String(t('common:Edit'))}
                      >
                        <AppIcon name="Pencil" size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenDeleteDialog(value)}
                        aria-label={String(t('common:Delete'))}
                      >
                        <AppIcon name="Trash2" size={16} className="text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onOpenChange={(open) => { if (!open && !loading) handleCloseDialog(); }}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>
              {dialogType === 'add' ? t('Add Recognition Value') : t('Edit Recognition Value')}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-4">
            <DialogErrorAlert error={dialogError.error} onDismiss={dialogError.clear} className="mb-0" />
            <Input
              label={t('Name')}
              required
              value={currentInput.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCurrentInput({ ...currentInput, name: e.target.value })}
              error={!currentInput.name.trim() && currentInput.name !== '' ? t('Name is required') : undefined}
            />
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                {t('Description')}
              </label>
              <textarea
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 min-h-[80px]"
                value={currentInput.description || ''}
                onChange={(e) => setCurrentInput({ ...currentInput, description: e.target.value })}
                rows={3}
              />
            </div>
          </DialogBody>
          <DialogFooter className="justify-center">
            <Button onClick={handleSave} disabled={loading || !currentInput.name.trim()} loading={loading}>
              {t('common:Save')}
            </Button>
            <Button variant="outline" onClick={handleCloseDialog} disabled={loading}>
              {t('common:Cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(open) => { if (!open && !loading) handleCloseDeleteDialog(); }}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{t('Confirm Delete')}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <DialogErrorAlert error={deleteDialogError.error} onDismiss={deleteDialogError.clear} className="mb-0" />
            <p className="text-sm text-foreground">
              {t('Are you sure you want to delete')} &quot;{selectedValue?.name}&quot;?
            </p>
          </DialogBody>
          <DialogFooter className="justify-center">
            <Button variant="destructive" onClick={handleDelete} disabled={loading} loading={loading}>
              {t('common:Delete')}
            </Button>
            <Button variant="outline" onClick={handleCloseDeleteDialog} disabled={loading}>
              {t('common:Cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
      </div>
    </PageLayout>
  );
};

export default RecognitionValues;
